export const longPollHoldSeconds = 30;

const mxSubscriberId = Date.now() + '-' + Math.floor(1e4 * Math.random());


let pipeMap = {};
let meta = undefined;
let simHost;
let abortCount = 0;



//the fields that can optionally be populated in accessors param passed when makeSimFirestore is called 
const accessorFields = ['safeGetRobotId', 'getAutoPrioConfig', 'getBotMxPollTOTicks', 'getBotMxAddTOTicks'];
let accessors = {};
accessorFields.forEach(f => accessors[f] = (() => undefined));



function stdParms() {
  return {};
}


export function opts(parms) {
  let result = {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...stdParms(), ...parms, meta, robotId:accessors.safeGetRobotId() }),
  };
  return result;
}


//supplied meta data will be added to server calls.
export function setMeta(metaParam) {
  meta = metaParam;
}

/*
    On success, returns json || {}.

    handlers: optional; can consist of (1) success function, or (2) object with various combinations of the below handler:
        success: on success, called with json || {}
        bad: {err, netResult, logStr} called if exception or !.ok or .status!=200; err and netResult are mut exclusive; logStr is loggable string
        notOk: (netResult) called if !.ok or .status!=200
        caught: (err) called if exception
        - bad and (notOk||caught) are mutually exclusive.
        - If no error handling specified, errors are snuffed.
        final: (wasGood) called after other handlers.

    If handlers are specified, the outcome-specific ones will have been called by the time this function returns.
*/
export async function clearFetch(url, options, handlers, timeoutTicks) {
  let success, bad, notOk, caught, final;
  if (handlers) {
    if (typeof handlers == 'function') success = handlers;
    else {
      let allowed = 'success,bad,notOk,caught,final'.split(',');
      Object.keys(handlers).forEach((k) => {
        if (allowed.indexOf(k) < 0) throw new Error('clearFetch: unrecognized handler: ' + k);
      });
      success = handlers.success;
      bad = handlers.bad;
      notOk = handlers.notOk;
      caught = handlers.caught;
      final = handlers.final;

      if (bad && (notOk || caught)) throw new Error('clearFetch: bad params');
    }
  }

  options = options || {};

  let abortTimeout, aborted = false;
  if(!isNaN(timeoutTicks)) {

    //We're deliberately not using options.signal=AbortSignal.timeout(ticks) 
    // because ios doesn't appear to support it until 18.x.
    try {
      timeoutTicks = Math.max(0, Number(timeoutTicks || 0));
      let abortController = new AbortController();
      options.signal = abortController.signal;

      abortTimeout = setTimeout(() => {

        abortTimeout = undefined;
        abortController.abort();
        aborted = true;
        ++abortCount;

        //doElasticLogError({what:'netAbort', msg:`aborted at ${Math.round(timeoutTicks / 100)/10}s (total=${abortCount}) ` + url, url, timeoutTicks});
      }, timeoutTicks);
    }
    catch(err) {
      delete options.signal;
      //doElasticLogError({what:'netTOSetupErr', msg:`Error setting up network timeout: ` + err});
    }
  }
  let netResult;
  try {
    netResult = await fetch(url, options);
  }
  catch (err) {
    if ('' + err == 'TypeError: Failed to fetch') err = 'url unreachable';

    if (bad) bad({ err, logStr: '' + err });
    else if (caught) caught(err);

    if (final) final(false);

    return undefined;
  }
  finally {
    if(abortTimeout) {
      clearTimeout(abortTimeout);
    }
  }

  if (!netResult.ok || netResult.status != 200) {
    if (bad) bad({ netResult, logStr: `${netResult.status} ok=${netResult.ok} ${netResult.statusText}` });
    else if (notOk) notOk(netResult);

    if (final) final(false);

    return undefined;
  }

  let json = (await netResult.json()) || {};
  if (success) success(json);

  if (final) final(true);

  return json || {};
}


export function getSimHost() {
  if (!simHost) throw new Error('getSimHost called with no sim host');
  return simHost;
}

/*
Opens a socket subscription.

@param channel is an object with a 'type' field ({type}).  The value of the type field
  must be one of several known types (see check inside the routine).
@param cb is a callback.
*/
export function openSocket(channel, cb) {
  let { server, type } = channel;
  return mxSubscribe(server, type, cb, false);
}


export function makeSimFirestore(firestoreConfig, accessorsParam) {
  if (!firestoreConfig.simServer) throw new Error('makeSimFirestore called with non-sim config');
  simHost = firestoreConfig.simServer;

  //Populate default accessors struct/procs to avoid need to test truthiness elsewhere.
  //NB: makeSimFirestore is presumed to be called at most once.
  accessors = accessorsParam || {};
  accessorFields.forEach(f => accessors[f] = accessors[f] || (() => undefined));

  const callSimTimeout = 1000 * 10;
  let callSim = (path, parms = {}, handlers) =>
    clearFetch(`${firestoreConfig.simServer}/${path}`, opts(parms), handlers, callSimTimeout);

  function docStuff(path, docName, convertFunc) {
    return {
      get: async () => {
        let result;
        await callSim(
          'doc',
          { path, docName },
          {
            success: (json) => {
              result = { exists: true, data: () => json };
              if (convertFunc) {
                let converted = convertFunc(result, {});
                result = { exists: true, data: () => converted };
              }
            },
            bad: ({ err, netResult, logStr }) => {
              console.log(`** Error getting docName ${docName}: ${logStr}`);
              result = { exists: false, data: () => undefined };
            },
          },
        );
        return result;
      },

      set: async (delta) => {
        let result;
        await callSim(
          'doc_set',
          { path, docName, delta },
          {
            success: (json) => {
              result = { exists: true, data: () => json };
              if (convertFunc) {
                let converted = convertFunc(result, {});
                result = { exists: true, data: () => converted };
              }
            },
            bad: ({ err, netResult, logStr }) => {
              console.log(`** Error setting docName ${docName}: ${logStr}`);
              result = { exists: false, data: () => undefined };
            },
          },
        );
        return result;
      },

      onSnapshot: (options, cb) => {
        //return subscribeFsSim(callSim, path, options, cb, {type:'doc', docName});
        if (typeof options == 'function') {
          cb = options;
          options = undefined;
        }

        let flattened = [path, docName].filter((x) => x).join('/');
        return mxSubscribe(firestoreConfig.simServer, flattened, cb, true);
      },

      update: async (delta) => {
        let result;
        await callSim(
          'doc_update',
          { path, docName, delta },
          {
            success: (json) => {
              result = { exists: true, data: () => json };
              if (convertFunc) {
                let converted = convertFunc(result, {});
                result = { exists: true, data: () => converted };
              }
            },
            bad: ({ err, netResult, logStr }) => {
              console.log(`** Error getting docName ${docName}: ${logStr}`);
              result = { exists: false, data: () => undefined };
            },
          },
        );
        return result;
      },
    };
  }

  return {
    isSim: true,

    doc: (docName) => docStuff('', docName),

    collection: (path) => {
      return {
        //------------------
        doc: (docName) => docStuff(path, docName),
        //------------------
        withConverter: (converter) => {
          let convertFunc = converter.fromFirestore;
          return {
            doc: (docName) => docStuff(path, docName, convertFunc),
          };
        },
        //------------------
        get: async () => {
          let result;
          await callSim(
            'collection',
            { path },
            {
              success: (json) => {
                let docs = Object.keys(json).map((id) => ({ id, data: () => json[id] }));
                result = { exists: true, docs };
              },
              bad: ({ err, netResult, logStr }) => {
                console.log(`** Error getting collection ${path}: ${logStr}`);
                result = { exists: false, data: () => undefined };
              },
            },
          );
          return result;
        },
        //------------------
        orderBy: (attrName, attrOrder) => {
          return {
            limit: (limit) => {
              return {
                onSnapshot: (options, cb) => {
                  if (typeof options == 'function') {
                    cb = options;
                    options = undefined;
                  }
                  return mxSubscribe(firestoreConfig.simServer, path, cb, true);
                },

                get: async () => {
                  let result;
                  await callSim(
                    'collection',
                    { path },
                    {
                      success: (json) => {
                        let docs = Object.keys(json).map((id) => ({ id, data: () => json[id] }));
                        if(limit < docs.length)
                          docs = docs.slice(0, limit);
                        result = { exists: true, docs };
                      },
                      bad: ({ err, netResult, logStr }) => {
                        console.log(`** Error getting collection ${path}: ${logStr}`);
                        result = { exists: false, data: () => undefined };
                      },
                    },
                  );
                  return result;
                }
              };
            },
          };
        },
        //------------------
        onSnapshot: (options, cb) => {
          if (typeof options == 'function') {
            cb = options;
            options = undefined;
          }
          return mxSubscribe(firestoreConfig.simServer, path, cb, true);
        },
        //------------------
      };
    },

    //TODO: actually make a transaction
    runTransaction: (cb) => {
      let tx = {
        get: async (ref) => {
          return ref.get();
        },
        set: async (ref, delta) => {
          return ref.set(delta);
        },
        update: async (ref, delta) => {
          ref.update(delta);
        },
      };

      cb(tx);
    },
  };
}

function makeSubscriber(callFunc, resultFunc) {
  let subscribed = true,
    pollTimer;
  let subscriptionId = Date.now() + '-' + Math.floor(1e4 * Math.random());
  let sequentialFailures = 0;

  async function poll() {
    if (!subscribed) return;

    let result;
    await callFunc(subscriptionId, {
      success: (json) => {
        sequentialFailures = 0;
        result = json;
      },
      bad: ({ err, netResult, logStr }) => {
        ++sequentialFailures;
        console.log(`** subscription poll error: ${logStr}`);
      },
    });

    if (subscribed) {
      if (result) {
        try {
          resultFunc(result);
        } catch (err) {
          console.log(err.stack.toString());
          console.log(`** subscription cb error: ` + err);
        }
      }

      let timeout = 0;
      if (sequentialFailures > 15) timeout = 1000 * 4;
      else if (sequentialFailures > 5) timeout = 1000;

      pollTimer = setTimeout(poll, timeout);
    }
  }

  pollTimer = setTimeout(poll);

  return () => {
    subscribed = false;
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = undefined;
    }
  };
}




export function mxSubscribe(host, type, cb, isFS = false) {

  if (!cb) debugger;

  let typeSubscriberId = Date.now() + '-' + Math.floor(1e4 * Math.random());
  let match = /^(http:\/\/[^/]+)/.exec(host);
  match = match || /^(https:\/\/[^/]+)/.exec(host);
  if (!match) throw new Error('startMxPipe: unexpected host: ' + host);

  let pipeMapEntry = pipeMap[host];

  if (!pipeMapEntry) {
    let subscribed = true, pollTimer, sequentialFailures = 0;

    const pipeUrl = host + (host.endsWith('/') ? '' : '/') + 'mxPoll';
    async function poll() {
      if (!subscribed) return;

      let timeOutTicks = accessors.getBotMxPollTOTicks();

      let typeList;
      await clearFetch(
        pipeUrl, 
        opts({ subscriptionId: mxSubscriberId, types: Object.keys(pipeMapEntry.types) }), 
        {
          success: (json) => {
            sequentialFailures = 0;
            typeList = json.typeList || [];
          },
          bad: ({ err, netResult, logStr }) => {
            ++sequentialFailures;
            console.log(`** mxPoll error: ${logStr}`);
          },
        },
        timeOutTicks
      );
      if (!subscribed) return;

      if (typeList) {
        for (let { type, data } of typeList) {
          let typeEntry = pipeMapEntry.types[type];

          if (!typeEntry) {
            debugger; //we have no basis for knowing if this is firestore data or not
            console.log('** DROPPING DATA of type ' + type);
            continue;
          }

          // console.log('consuming ' + typeEntry.isFS + ' ' + type + ' ' + JSON.stringify(data, false, 2));

          if (typeEntry.isFS) {
            if (Array.isArray(data)) {
              data = { docs: data.map(({ docId, docData }) => ({ exists: true, id: docId, data: () => docData })) };
            } else {
              let dataRef = data;
              data = { exists: true, data: () => dataRef };
            }
          }

          typeEntry.data = data;

          if (!typeEntry) {
            console.log('** mxPoll: no entry for type: ' + type);
          }
          else {
            for (let listener of typeEntry.listeners) {
              try {
                listener.cb(typeEntry.data);
              } catch (err) {
                console.log(err.stack.toString());
                console.log(`** mxPoll cb error: ` + err);
              }
            }
          }
        }
      }

      let timeout = 0;
      if (sequentialFailures > 15) timeout = 1000 * 4;
      else if (sequentialFailures > 5) timeout = 1000;
      pollTimer = setTimeout(poll, timeout);
    }

    let close = () => {
      subscribed = false;
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = undefined;
      }
    };

    pipeMapEntry = pipeMap[host] = { close, types: {} };
    pollTimer = setTimeout(poll); //begin polling
  }

  let typeInfo = pipeMapEntry.types[type];
  if (!typeInfo) {
    //create local entry
    typeInfo = pipeMapEntry.types[type] = { data: undefined, listeners: [], isFS };

    typeInfo.listeners.push({ typeSubscriberId, cb });

    let timeOutTicks = accessors.getBotMxAddTOTicks();

    //Inform remote end that we want this type subscribed.
    clearFetch(
      host + (host.endsWith('/') ? '' : '/') + 'mxAddType', 
      opts({ type, subscriptionId: mxSubscriberId }), 
      {
        bad: ({ err, netResult, logStr }) => {
          //Even if this call doesn't make it through, the next mxPoll will inform the 
          //server of all the types we're interested in; that said, the benefit of this call
          //going through is that the client won't wait til next mxPoll before getting initial data.
          console.log('** ERROR adding type ' + type + ': ' + logStr);
        }
      },
      timeOutTicks);
  }
  else {
    if (isFS != typeInfo.isFS) throw new Error('inconsistent firestore client orientations');
    typeInfo.listeners.push({ typeSubscriberId, cb });

    if (typeInfo.data) {
      //schedule an initial send of the data we have to the new subscriber
      setTimeout(() => cb(typeInfo.data));
    }
  }

  return () => {
    let typeInfo = pipeMap[host].types[type];
    if (typeInfo) {
      let idx = typeInfo.listeners.findIndex((listener) => listener.typeSubscriberId == typeSubscriberId);
      if (idx < 0) {
        console.log(
          '** NB: mxSubscribe (removal cb): Did not find typeInfo.listeners member with specified typeSubscriberId',
        );
      }
      else {
        typeInfo.listeners.splice(idx, 1);
        if (!typeInfo.listeners.length) {
          delete pipeMap[host].types[type]; //so our next poll roundtrip informs the server we no longer want this info
        }
      }
    }
    else {
      console.log('** NB: mxSubscribe (removal cb): Did not find typeInfo for specified type: ' + type);
    }
  };
}
